import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dices, Save, Plus, Pencil, Trash2, Layers } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, NeonPrice, Spinner, Pill } from "../../components/Neon";
import { gbp } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";

const EMPTY = { name: "", price: 9.99, monthly_prize: 5000, free_tickets: 10, draw_name: "Monthly Members Draw", perks: [], active: true };

async function loadAdminPlans() {
  try {
    const { data } = await api.get("/admin/subscription-plans");
    if (Array.isArray(data)) return { multi: true, plans: data };
  } catch (e) { /* not deployed -> fallback */ }
  return { multi: false, plans: [] };
}

const In = ({ label, ...p }) => (
  <div><label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{label}</label>
    <input className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" {...p} /></div>
);

export default function AdminSubscriptions() {
  const [multi, setMulti] = useState(false);
  const [plans, setPlans] = useState([]);       // multi mode
  const [settings, setSettings] = useState(null); // single mode
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = async () => {
    const [pl, l] = await Promise.all([
      loadAdminPlans(),
      api.get("/admin/subscriptions").then((r) => r.data).catch(() => []),
    ]);
    setSubs(l || []);
    setMulti(pl.multi);
    if (pl.multi) { setPlans(pl.plans); }
    else {
      const s = await api.get("/admin/subscription-settings").then((r) => r.data).catch(() => null);
      setSettings(s);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ---- single-plan mode ----
  const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : (e.target.type === "number" ? Number(e.target.value) : e.target.value) }));
  const saveSettings = async () => {
    setSaving(true);
    try { await api.put("/admin/subscription-settings", { price: settings.price, monthly_prize: settings.monthly_prize, free_tickets: settings.free_tickets, draw_name: settings.draw_name, active: settings.active }); toast.success("Subscription settings saved."); load(); }
    catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };

  // ---- multi-plan mode ----
  const openNew = () => { setEditId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setF({ ...EMPTY, ...p, perks: p.perks || [] }); setOpen(true); };
  const savePlan = async () => {
    const payload = { ...f, price: Number(f.price), monthly_prize: Number(f.monthly_prize), free_tickets: Number(f.free_tickets), perks: typeof f.perks === "string" ? f.perks.split("\n").map((x) => x.trim()).filter(Boolean) : f.perks };
    setSaving(true);
    try {
      if (editId) await api.put(`/admin/subscription-plans/${editId}`, payload);
      else await api.post("/admin/subscription-plans", payload);
      toast.success(editId ? "Tier updated." : "Tier created."); setOpen(false); load();
    } catch (e) { toast.error(getErr(e)); } finally { setSaving(false); }
  };
  const delPlan = async (p) => { if (!window.confirm(`Delete the "${p.name}" tier?`)) return; try { await api.delete(`/admin/subscription-plans/${p.id}`); toast.success("Tier deleted."); load(); } catch (e) { toast.error(getErr(e)); } };

  const runDraw = async () => {
    if (!window.confirm("Run the monthly members draw now?")) return;
    setDrawing(true);
    try { const { data } = await api.post("/admin/subscription-draw"); toast.success(`Draw complete${data?.winner ? `: ${data.winner}` : ""}.`); load(); }
    catch (e) { toast.error(getErr(e)); } finally { setDrawing(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-subscriptions">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-2xl text-white">Members Club {multi && <Pill color="green">Multi-tier</Pill>}</h2>
        {multi && <NeonButton variant="pink" onClick={openNew} data-testid="add-plan-button"><Plus className="h-4 w-4" /> New Tier</NeonButton>}
      </div>

      {!multi && (
        <div className="mt-2 rounded-lg border border-[rgba(255,184,0,.35)] bg-[rgba(255,184,0,.06)] px-4 py-3 text-sm text-[var(--neon-gold)] flex items-start gap-2">
          <Layers className="h-4 w-4 mt-0.5" />
          <span>Your backend currently supports a single members plan. To list <b>multiple tiers</b> at different prices/rewards (synced with the mobile app), deploy the multi-tier backend patch — this screen will automatically switch to full tier management.</span>
        </div>
      )}

      {multi ? (
        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <NeonCard key={p.id} variant="green" innerClassName="p-5">
              <div className="flex items-center justify-between">
                <p className="font-display uppercase text-lg text-white">{p.name}</p>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-white/10 text-[var(--neon-cyan)]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => delPlan(p)} className="p-1.5 rounded hover:bg-white/10 text-[var(--neon-pink)]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-2"><NeonPrice value={gbp(p.price)} color="green" className="text-3xl" /> <span className="text-[var(--text-muted)] text-sm">/mo</span></div>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{p.free_tickets} free tickets · {gbp(p.monthly_prize)} {p.draw_name}</p>
              <div className="mt-2"><Pill color={p.active ? "green" : "grey"}>{p.active ? "Active" : "Inactive"}</Pill></div>
            </NeonCard>
          ))}
        </div>
      ) : settings ? (
        <NeonCard variant="green" className="mt-4" innerClassName="p-6 max-w-xl">
          <p className="font-display uppercase text-xl text-white">Plan Settings</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <In label="Price (£)" type="number" step="0.01" value={settings.price} onChange={setS("price")} data-testid="sub-price" />
            <In label="Monthly prize (£)" type="number" value={settings.monthly_prize} onChange={setS("monthly_prize")} />
            <In label="Free tickets" type="number" value={settings.free_tickets} onChange={setS("free_tickets")} />
            <In label="Draw name" value={settings.draw_name} onChange={setS("draw_name")} />
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={settings.active} onChange={setS("active")} className="accent-[var(--neon-green)] h-4 w-4" /> Plan active</label>
          <NeonButton variant="pink" className="mt-4" onClick={saveSettings} loading={saving} data-testid="save-sub-settings"><Save className="h-4 w-4" /> Save</NeonButton>
        </NeonCard>
      ) : null}

      <NeonCard variant="flow" className="mt-5" innerClassName="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-display uppercase text-xl text-white">Monthly Draw</p>
            <p className="mt-1 text-sm text-[var(--text-muted)]">{subs.length} active subscriber(s).</p>
          </div>
          <NeonButton variant="outlinePink" onClick={runDraw} loading={drawing} data-testid="run-sub-draw"><Dices className="h-4 w-4" /> Run Members Draw</NeonButton>
        </div>
      </NeonCard>

      <NeonCard variant="soft" className="mt-5" innerClassName="p-0">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[var(--text-muted)] border-b border-white/10"><th className="p-3">Subscriber</th><th>Email</th><th className="p-3">Status</th></tr></thead>
          <tbody>{subs.length === 0 ? <tr><td colSpan={3} className="p-4 text-center text-[var(--text-muted)]">No active subscribers.</td></tr> : subs.map((s, i) => (<tr key={i} className="border-b border-white/5"><td className="p-3 text-white">{s.full_name || s.user_id}</td><td className="text-[var(--text-muted)]">{s.email}</td><td className="p-3 text-[var(--neon-green)]">{s.plan_name || s.status || "active"}</td></tr>))}</tbody>
        </table></div>
      </NeonCard>

      {/* Multi-tier editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--surface)] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display uppercase text-2xl">{editId ? "Edit" : "New"} Tier</DialogTitle><DialogDescription className="text-[var(--text-muted)]">Set a price, reward and perks for this membership tier.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <In label="Tier name" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <In label="Price (£/mo)" type="number" step="0.01" value={f.price} onChange={(e) => setF((s) => ({ ...s, price: e.target.value }))} />
              <In label="Monthly prize (£)" type="number" value={f.monthly_prize} onChange={(e) => setF((s) => ({ ...s, monthly_prize: e.target.value }))} />
              <In label="Free tickets" type="number" value={f.free_tickets} onChange={(e) => setF((s) => ({ ...s, free_tickets: e.target.value }))} />
              <In label="Draw name" value={f.draw_name} onChange={(e) => setF((s) => ({ ...s, draw_name: e.target.value }))} />
            </div>
            <div><label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Perks (one per line)</label>
              <textarea rows={4} value={Array.isArray(f.perks) ? f.perks.join("\n") : f.perks} onChange={(e) => setF((s) => ({ ...s, perks: e.target.value }))} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
            <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} className="accent-[var(--neon-green)] h-4 w-4" /> Active</label>
          </div>
          <DialogFooter>
            <NeonButton variant="ghost" onClick={() => setOpen(false)}>Cancel</NeonButton>
            <NeonButton variant="pink" onClick={savePlan} loading={saving} data-testid="save-plan">{editId ? "Save" : "Create"}</NeonButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
